import { merge } from '../../helper';

import type { HighlightConfig, HighlightMatch } from './types';

// ----------------------------------------------------------------------------

function getConfig(
    config: HighlightConfig = {},
): Required<Pick<HighlightConfig, 'tag' | 'className'>> & HighlightConfig {
    const defConfig: Required<Pick<HighlightConfig, 'tag' | 'className'>> & HighlightConfig = {
        tag: 'span',
        className: 'highlight',
        caseSensitive: false,
        wholeWord: false,
        excludeSelectors: [],
    };

    return merge(defConfig, config) as Required<Pick<HighlightConfig, 'tag' | 'className'>> & HighlightConfig;
}

function escapeSelector(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }

    return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function isWordChar(value: string): boolean {
    return /\w/.test(value);
}

function isWholeWordMatch(value: string, index: number, length: number): boolean {
    const before = index === 0 ? '' : value[index - 1];
    const afterIndex = index + length;
    const after = afterIndex >= value.length ? '' : value[afterIndex];

    return !(before && isWordChar(before)) && !(after && isWordChar(after));
}

function getRegex(find: string, config: HighlightConfig): RegExp | null {
    if (config.regex instanceof RegExp) {
        const flags = new Set(config.regex.flags.split(''));

        if (config.caseSensitive === false) {
            flags.add('i');
        }

        flags.delete('g');

        const source = config.wholeWord === true ? `\\b(?:${config.regex.source})\\b` : config.regex.source;

        return new RegExp(source, Array.from(flags).join(''));
    }

    if (config.regex === true) {
        const flags = config.caseSensitive === false ? 'i' : '';
        const source = config.wholeWord === true ? `\\b${find}\\b` : find;

        return new RegExp(source, flags);
    }

    return null;
}

function getTextMatchData(value: string, find: string, config: HighlightConfig): HighlightMatch | null {
    const sourceValue = config.caseSensitive === false ? value.toLowerCase() : value;
    const sourceFind = config.caseSensitive === false ? find.toLowerCase() : find;

    let fromIndex = 0;

    while (fromIndex <= sourceValue.length) {
        const index = sourceValue.indexOf(sourceFind, fromIndex);

        if (index < 0) {
            return null;
        }

        if (config.wholeWord !== true || isWholeWordMatch(value, index, find.length)) {
            return {
                index,
                text: value.slice(index, index + find.length),
            };
        }

        fromIndex = index + find.length;
    }

    return null;
}

function getMatchData(value: string, find: string, config: HighlightConfig): HighlightMatch | null {
    const regex = getRegex(find, config);

    if (regex != null) {
        regex.lastIndex = 0;

        const match = regex.exec(value);

        if (!match || typeof match.index !== 'number' || !match[0].length) {
            return null;
        }

        return {
            index: match.index,
            text: match[0],
        };
    }

    if (!find.length) {
        return null;
    }

    return getTextMatchData(value, find, config);
}

function isHighlightWrapper(element: Element, config: HighlightConfig): boolean {
    return (
        element.tagName.toLowerCase() === (config.tag ?? 'span').toLowerCase() &&
        element.classList.contains(config.className ?? 'highlight')
    );
}

function isExcludedElement(element: Node, config: HighlightConfig): boolean {
    const selectors = config.excludeSelectors ?? [];

    if (!selectors.length) {
        return false;
    }

    const targetElement =
        element.nodeType === Node.ELEMENT_NODE
            ? (element as Element)
            : ((element.parentElement as Element | null) ?? null);

    if (!targetElement) {
        return false;
    }

    try {
        return selectors.some((selector): any => Boolean(selector) && targetElement.closest(selector) != null);
    } catch {
        return false;
    }
}

function unwrapElement(element: HTMLElement): void {
    const parentElement = element.parentElement;

    if (!parentElement) {
        return;
    }

    const fragment = document.createDocumentFragment();

    while (element.firstChild) {
        fragment.appendChild(element.firstChild);
    }

    parentElement.replaceChild(fragment, element);
    parentElement.normalize();
}

// ----------------------------------------------------------------------------

export { getConfig, escapeSelector, getMatchData, isExcludedElement, isHighlightWrapper, unwrapElement };
